/**
 * Exact integer arithmetic primitives.
 *
 * Spec §11A: "All amounts integer paise; never use floating point for money."
 * Every arithmetic operation in this package goes through one of these helpers,
 * so a stray `0.1 + 0.2` can never reach a GST invoice.
 */
/**
 * Throws unless `value` is a safe integer.
 *
 * @param label human-readable name used in the error message.
 */
export function assertInteger(value, label) {
    if (typeof value !== "number" || !Number.isFinite(value)) {
        throw new Error(`${label} must be a finite number, received ${String(value)}`);
    }
    if (!Number.isInteger(value)) {
        throw new Error(`${label} must be an integer — money is paise, weight is grams, quantity is milli-units; received ${value}`);
    }
    if (!Number.isSafeInteger(value)) {
        throw new Error(`${label} is outside the safe integer range, received ${value}`);
    }
}
/** Throws unless `value` is a safe integer that is >= 0. */
export function assertNonNegativeInteger(value, label) {
    assertInteger(value, label);
    if (value < 0) {
        throw new Error(`${label} must not be negative, received ${value}`);
    }
}
/**
 * Multiplies two integers, refusing to return a result that has silently lost
 * precision by exceeding `Number.MAX_SAFE_INTEGER`.
 */
export function mulExact(a, b, label) {
    assertInteger(a, `${label} (left operand)`);
    assertInteger(b, `${label} (right operand)`);
    const product = a * b;
    if (!Number.isSafeInteger(product)) {
        throw new Error(`${label} overflowed exact integer arithmetic: ${a} * ${b} = ${product} exceeds Number.MAX_SAFE_INTEGER`);
    }
    return product;
}
/**
 * Exact integer division rounded half-up — the single rounding rule used by the
 * whole engine (spec §4, "All per-line tax amounts rounded to the nearest paise
 * (integer) using round-half-up").
 *
 * Ties round away from zero, so `divRoundHalfUp(5, 10) === 1` and
 * `divRoundHalfUp(-5, 10) === -1`.
 *
 * The quotient is derived with a floating divide and then *corrected* by exact
 * integer subtraction, so a double-precision off-by-one on very large operands
 * cannot survive.
 *
 * @throws if either argument is not a safe integer, or the denominator is <= 0.
 */
export function divRoundHalfUp(numerator, denominator) {
    assertInteger(numerator, "divRoundHalfUp numerator");
    assertInteger(denominator, "divRoundHalfUp denominator");
    if (denominator <= 0) {
        throw new Error(`divRoundHalfUp denominator must be greater than zero, received ${denominator}`);
    }
    const isNegative = numerator < 0;
    const abs = isNegative ? -numerator : numerator;
    let quotient = Math.floor(abs / denominator);
    let remainder = abs - quotient * denominator;
    // Correct any floating-point drift in the initial estimate.
    while (remainder < 0) {
        quotient -= 1;
        remainder += denominator;
    }
    while (remainder >= denominator) {
        quotient += 1;
        remainder -= denominator;
    }
    // Half-up on the magnitude: remainder/denominator >= 0.5
    if (remainder * 2 >= denominator) {
        quotient += 1;
    }
    return isNegative ? -quotient : quotient;
}
/** Paise in one rupee. */
export const PAISE_PER_RUPEE = 100;
/**
 * Rounds a paise amount to the nearest whole rupee, half-up (Section 170 CGST
 * rounding, spec §4). Always returns a multiple of 100.
 */
export function roundToNearestRupeePaise(paise) {
    return divRoundHalfUp(paise, PAISE_PER_RUPEE) * PAISE_PER_RUPEE;
}
//# sourceMappingURL=rounding.js.map